import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import client from '@/ports/api-client'
import {
  saveStandAloneTrustScore as save,
  updateStandAloneTrustScore as update,
  uploadTenantConsent as uploadConsent,
  unlockStandAloneTrustScore as unlock,
  retrieveLookupCountForTrustScore as retrieveLookupCount
} from '@/models/application/ports'
import { purchaseStandaloneWithDiscount } from '@/models/plans/apis'
import { retrieveAddress } from '@/models/profile/ports'
import queryClient from '@/components/query-client'

type PurchaseData = {
  paymentMethodId: string
  discountCode: string
  onSuccess: () => void
}

const useGetLookupCount = () => {
  const { data, isLoading, refetch } = useQuery({
    queryFn: () => retrieveLookupCount(),
    queryKey: ['lookup-count'],
    refetchOnWindowFocus: false
  })

  return { data: data?.body?.remaining, isLoading, refetch }
}

const getStandAloneTrustScoreSin = async (trustScoreId: string) => {
  try {
    return await client.get(`/trust_score/standalone/${trustScoreId}/sin_reveal`)
  } catch (error) {
    return error
  }
}

const getStandAloneTrustScoreDliverLicense = async (trustScoreId: string) => {
  try {
    return await client.get(`/trust_score/standalone/${trustScoreId}/dl_reveal`)
  } catch (error) {
    return error
  }
}

const useGetStandAloneTrustScoreSin = () => {
  const mutation = useMutation({
    mutationFn: (trustScoreId: string) => {
      return getStandAloneTrustScoreSin(trustScoreId)
    }
  })

  const { mutateAsync, isPending } = mutation
  return { mutateAsync, isPending }
}
const useGetStandAloneTrustScoreDl = () => {
  const mutation = useMutation({
    mutationFn: (trustScoreId: string) => {
      return getStandAloneTrustScoreDliverLicense(trustScoreId)
    }
  })

  const { mutateAsync, isPending } = mutation
  return { mutateAsync, isPending }
}

const getStandAloneTrustScore = async (trustScoreId: string) => {
  return await client.get(`/trust_score/standalone/${trustScoreId}`)
}

const useGetStandAloneTrustScore = (trustScoreId: string) => {
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: [`standalone-trust-score`, trustScoreId],
    queryFn: () => getStandAloneTrustScore(trustScoreId),
    refetchOnWindowFocus: false,
    staleTime: 60000 * 5,
    enabled: !!trustScoreId
  })

  return { data, isLoading, refetch, error }
}

const getTenantConsent = async (trustScoreId: string) => {
  try {
    return await client.get(`/trust_score/standalone/${trustScoreId}/consent`)
  } catch (error) {
    return error
  }
}
const useGetTenantConsent = (trustScoreId: string) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [`tenant-consent`, trustScoreId],
    queryFn: () => getTenantConsent(trustScoreId),
    refetchOnWindowFocus: false,
    staleTime: 60000 * 5,
    enabled: !!trustScoreId
  })

  return { data, isLoading, refetch }
}

const useUploadTenantConsent = () => {
  const mutation = useMutation({
    mutationFn: ({ fileData, trustScoreId }: { fileData: any; trustScoreId: number }) => {
      return uploadConsent({ file: fileData }, { trustScoreId })
    },
    onSuccess: async (result, variables) => {
      queryClient.setQueryData([`tenant-consent`, variables.trustScoreId.toString()], {
        consent: result?.body.file
      })
    }
  })

  const { mutate, mutateAsync, isPending } = mutation
  return { mutate, mutateAsync, isPending }
}

const useDeleteTenantConsent = () => {
  const mutation = useMutation({
    mutationFn: (trustScoreId: string) => {
      return client.delete(`/trust_score/standalone/${trustScoreId}/consent`)
    },
    onSuccess: async result => {
      queryClient.invalidateQueries({ queryKey: [`tenant-consent`] })
    }
  })
  return mutation
}

const useRetrieveAddress = () => {
  const mutation = useMutation({
    mutationFn: (addressId: string) => {
      return retrieveAddress({ addressId })
    },
    onSuccess: async result => {
      return result
    }
  })

  const { mutateAsync, isPending } = mutation
  return { mutateAsync, isPending }
}

const useSaveStandAloneTrustScore = () => {
  const mutation = useMutation({
    mutationFn: (trustScore: any) => {
      return save({ body: trustScore })
    },
    onSuccess: async result => {
      if (result?.response.ok) {
        queryClient.setQueryData(
          [`standalone-trust-score`, result?.body?.tenant?.basic_info?.id],
          result?.body
        )

        queryClient.invalidateQueries({
          queryKey: ['trustScoreList'],
          exact: false
        })
        return result
      }
    }
  })

  const { mutateAsync, isPending } = mutation
  return { mutateAsync, isPending }
}

const useUpdateStandAloneTrustScore = (trustScoreId: string) => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (trustScore: any) => {
      return update({ body: trustScore }, { trustScoreId })
    },
    onSuccess: async result => {
      if (result?.response?.ok) {
        queryClient.setQueryData([`standalone-trust-score`, trustScoreId], result?.body)
        queryClient.invalidateQueries({
          queryKey: ['trustScoreList'],
          exact: false
        })
        return result
      }
    }
  })

  const { mutateAsync, isPending } = mutation
  return { mutateAsync, isPending }
}
const updateStandAloneTrustScore = async (trustScore: any, trustScoreId: string) => {
  return update({ body: trustScore }, { trustScoreId })
}

const useHandleStandAlonePurchase = () => {
  const mutation = useMutation({
    mutationFn: (purchaseData: PurchaseData) => {
      return purchaseStandaloneWithDiscount(
        {
          plan_name: 'credit-check-single-package',
          payment_method_id: purchaseData.paymentMethodId
        },
        { couponCode: purchaseData.discountCode }
      )
    },
    onSuccess: async (result, variables) => {
      const response = result?.response
      if (response?.ok) {
        variables.onSuccess()
      }
    }
  })

  const { mutate, mutateAsync, isPending } = mutation
  return { mutate, mutateAsync, isPending }
}

const useUnlockStandaloneTrustScore = (
  trustScoreId: string,
  creditDetails: { planCreditChecks: number; purchasedCreditChecks: number }
) => {
  const usePlanCredits = creditDetails?.planCreditChecks >= 1
  const planSource = usePlanCredits ? 'credit_checks' : 'credit_checks_additional_packages'
  const mutation = useMutation({
    mutationFn: () => {
      return unlock({ body: { trust_score_request_source: planSource } }, { trustScoreId })
    },
    onSuccess: async (result, variables: { onSuccess: () => void }) => {
      const response = result?.response
      if (response?.ok) {
        queryClient.invalidateQueries({ queryKey: ['trustScoreList'] })
        queryClient.invalidateQueries({ queryKey: ['blocks'] })
        variables.onSuccess()
      }
    }
  })

  const { mutate, mutateAsync, isPending } = mutation
  return { mutate, mutateAsync, isPending }
}

const handleStandAlonePurchase = async (purchaseData: PurchaseData) => {
  return purchaseStandaloneWithDiscount(
    {
      plan_name: 'credit-check-single-package',
      payment_method_id: purchaseData.paymentMethodId
    },
    { couponCode: purchaseData.discountCode }
  )
}

export {
  useGetStandAloneTrustScore,
  updateStandAloneTrustScore,
  useSaveStandAloneTrustScore,
  useUpdateStandAloneTrustScore,
  useGetStandAloneTrustScoreSin,
  useGetStandAloneTrustScoreDl,
  useGetTenantConsent,
  useUploadTenantConsent,
  useDeleteTenantConsent,
  handleStandAlonePurchase,
  useHandleStandAlonePurchase,
  useUnlockStandaloneTrustScore,
  useGetLookupCount,
  useRetrieveAddress
}
